/**
 * SSOT: מיפוי הזמנות לטבלה, כרטיסים וכל תצוגה אחידה.
 * raw — אובייקט המקור מה-API (למודאל / CollapsedHotelRow).
 */

import { getLdsOrderPrimaryBadgeText } from "../ldsOrderStatus.js";
import { getPackageOrderCardSummary } from "./packageOrderCardSummary.js";
import { resolveLdsFinancialState } from "./resolveLdsFinancialState.js";

export function parseDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === "string" && dateStr.includes("/")) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      if (day && month >= 0 && year) {
        const date = new Date(year, month, day);
        date.setHours(0, 0, 0, 0);
        return date;
      }
    }
  }
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    date.setHours(0, 0, 0, 0);
    return date;
  }
  return null;
}

function usdToIls(usdAmount) {
  if (!usdAmount || usdAmount === 0) return 0;
  try {
    const rate = localStorage.getItem("currentRate");
    if (rate && rate !== "undefined") {
      return parseFloat(usdAmount) * parseFloat(rate);
    }
  } catch (err) {
    console.warn("normalizeOrders usdToIls:", err);
  }
  return parseFloat(usdAmount);
}

function ilsToUsd(ilsAmount) {
  if (!ilsAmount || ilsAmount === 0) return 0;
  try {
    const rate = localStorage.getItem("currentRate");
    if (rate && rate !== "undefined" && parseFloat(rate) > 0) {
      return parseFloat(ilsAmount) / parseFloat(rate);
    }
  } catch (err) {
    console.warn("normalizeOrders ilsToUsd:", err);
  }
  return parseFloat(ilsAmount);
}

function hotelSumPaidIls(order) {
  const pay = order.payments;
  if (Array.isArray(pay) && pay.length > 0) {
    return pay
      .filter(
        (p) =>
          p && (p.paid === true || p.paid === 1 || p.Paid === true)
      )
      .reduce(
        (acc, cur) =>
          acc +
          (parseFloat(cur.Amount ?? cur.amount ?? cur.AmountILS ?? 0) || 0),
        0
      );
  }
  const sp = parseFloat(order.sumpaid);
  return Number.isFinite(sp) ? sp : 0;
}

function hotelTotalPriceIls(order) {
  const raw =
    order.totalpricenow ??
    order.totalprice ??
    order.pricetotal ??
    order.packagepricedetails?.FinalPrice ??
    order.price;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/** מטבע חבילה מ-packagepricedetails (לרוב JSON string ב-API) — בלי זה Currency נשאר ריק וההזמנה נסמנת כ-ILS בטעות */
function hotelPackageCurrency(order) {
  let pd = order?.packagepricedetails;
  if (typeof pd === "string") {
    try {
      pd = JSON.parse(pd);
    } catch {
      pd = null;
    }
  }
  if (pd && typeof pd === "object" && pd.Currency != null && String(pd.Currency).trim()) {
    return String(pd.Currency).trim();
  }
  return "";
}

function hotelNameDisplay(order) {
  const v = order.hotelname ?? order.hotelName ?? order.HotelName ?? "";
  const s = String(v).trim();
  return s || "-";
}

function hotelMainContactDisplay(order) {
  const v =
    order.maincontact ??
    order.main_contact ??
    order.fullname ??
    "";
  const s = String(v).trim();
  return s || "-";
}

function hotelCommisions(order) {
  return order.commisions || order.commissions || null;
}

function hotelHandlingFeeRaw(order) {
  const v = order.handlingfee ?? order.handling_fee ?? order.handleFee;
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function hotelHandleFeeOption(order) {
  return (
    order.handle_fee_option ??
    order.handle_fee_type ??
    order.handleFeeOption ??
    null
  );
}

function hotelBasePriceIls(order) {
  const raw =
    order.packagepricedetails?.FinalPrice ??
    order.price ??
    order.packageprice ??
    order.totalpricenow ??
    order.totalprice ??
    order.pricetotal;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function computeHotelHrIls(order) {
  const op = order.ourprice;
  const hrFromOur =
    op?.hrCommission ?? op?.hr_commission ?? op?.HrCommission;
  if (
    hrFromOur != null &&
    hrFromOur !== "" &&
    Number.isFinite(parseFloat(hrFromOur))
  ) {
    const hrUsd = parseFloat(hrFromOur) || 0;
    const savedRate = order.rates?.savedRate || order.rates?.rate;
    if (savedRate && parseFloat(savedRate) > 0) {
      return hrUsd * parseFloat(savedRate);
    }
    return usdToIls(hrUsd);
  }
  const c = hotelCommisions(order);
  if (c == null || c.hr == null || String(c.hr).trim() === "") {
    return null;
  }
  const hrValue = parseFloat(c.hr) || 0;
  if (hrValue <= 0) return null;
  const baseIls = hotelBasePriceIls(order);
  const savedRate = order.rates?.savedRate || order.rates?.rate;
  if (hrValue > 0 && hrValue < 100) {
    if (baseIls <= 0) return null;
    return (baseIls * hrValue) / 100;
  }
  if (savedRate && parseFloat(savedRate) > 0) {
    return hrValue * parseFloat(savedRate);
  }
  return usdToIls(hrValue);
}

function hotelBookingUsdRate(order) {
  let rates = order?.rates;
  if (typeof rates === "string") {
    try {
      rates = JSON.parse(rates);
    } catch {
      rates = null;
    }
  }
  const candidates = [
    rates?.savedRate,
    rates?.rate,
    rates?.USD,
    rates?.usd,
    order?.exchange_rate_at_booking,
  ];
  for (const c of candidates) {
    const n = parseFloat(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function computeHotelHandlingIls(order) {
  const hf = hotelHandlingFeeRaw(order);
  if (hf === null) return null;
  const opt = hotelHandleFeeOption(order);
  const savedRate = hotelBookingUsdRate(order);
  const baseIls = hotelBasePriceIls(order);

  if (opt === "%") {
    let basePriceUsd = 0;
    if (order.ourprice?.priceWithCommission != null) {
      basePriceUsd = parseFloat(order.ourprice.priceWithCommission) || 0;
    } else if (baseIls > 0) {
      if (savedRate && parseFloat(savedRate) > 0) {
        basePriceUsd = baseIls / parseFloat(savedRate);
      } else {
        basePriceUsd = ilsToUsd(baseIls);
      }
    } else {
      return null;
    }
    const handlingFeeUsd = (basePriceUsd * hf) / 100;
    if (savedRate && parseFloat(savedRate) > 0) {
      return handlingFeeUsd * parseFloat(savedRate);
    }
    return usdToIls(handlingFeeUsd);
  }
  if (opt === "+" || opt === "plus") {
    if (savedRate && parseFloat(savedRate) > 0) {
      return Math.round(hf * parseFloat(savedRate));
    }
    return usdToIls(hf);
  }
  if (savedRate && parseFloat(savedRate) > 0) {
    return Math.round(hf * parseFloat(savedRate));
  }
  return usdToIls(hf);
}

function hotelAgentNameString(order) {
  if (!order.agentName) return "-";
  if (typeof order.agentName === "object") {
    const a = order.agentName;
    return a.firstName && a.lastName
      ? `${a.firstName} ${a.lastName}`
      : a.firstName || a.lastName || "-";
  }
  return order.agentName;
}

function ldsCommisions(order) {
  return order.commisions || order.commissions || null;
}

export function normalizeHotelOrder(order, index = 0) {
  const agentName = hotelAgentNameString(order);
  const hrIls = computeHotelHrIls(order);
  const handlingIls = computeHotelHandlingIls(order);
  const paidAmount = hotelSumPaidIls(order);
  const totalAmount = hotelTotalPriceIls(order);
  const remainingAmount = totalAmount - paidAmount;
  const currency = (
    hotelPackageCurrency(order) ||
    order.currency ||
    "ILS"
  ).toString();

  const row = {
    id: order.id ?? order.orderid,
    category: "מלונות",
    orderId: order.orderid || order.id || "-",
    bookingRef: order.booking_ref ?? order.bookingRef ?? null,
    hotelName: hotelNameDisplay(order),
    checkIn: order.checkin || order.check_in || "-",
    checkOut: order.checkout || order.check_out || "-",
    mainContact: hotelMainContactDisplay(order),
    status: order.cancelled
      ? "בוטלה"
      : order.paid
        ? "שולמה"
        : order.bank_transfer
          ? "ממתינה להעברה בנקאית"
          : "ממתינה לתשלום",
    totalAmount,
    paidAmount,
    remainingAmount,
    hrCommission: hrIls,
    handlingFee: handlingIls,
    createdAt: order.creationdate || "-",
    createdAtDate: order.creationdate ? parseDate(order.creationdate) : null,
    checkOutDate: order.checkout ? parseDate(order.checkout) : null,
    cityOrDestination: order.hoteladdress || "",
    image: null,
    currency,
    agentName,
    paid: !!(order.paid || false),
    raw: order,
    key: `hotel-${order.orderid || index}`,
    totalPrice: totalAmount,
    sumPaid: paidAmount,
  };
  return row;
}

function safeOrderResponseObjectForNormalize(order) {
  let r = order?.order_response;
  if (typeof r === "string" && r.trim().length > 2) {
    try {
      const p = JSON.parse(r);
      r = p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return r && typeof r === "object" && !Array.isArray(r) ? r : null;
}

function normalizeFlightLegValue(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  const paren = s.match(/\(([A-Z]{3})\)/);
  if (paren?.[1]) return paren[1];
  const code = s.match(/\b[A-Z]{3}\b/);
  if (code?.[0]) return code[0];
  return s;
}

function depCodeFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return normalizeFlightLegValue(
    leg.depAirport?.code ??
      leg.departureAirport?.code ??
      leg.origin ??
      leg.from ??
      leg.depCity ??
      leg.departure?.airportCode ??
      leg.depAirportCode ??
      leg.departureAirportCode
  );
}

function arrCodeFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return normalizeFlightLegValue(
    leg.arrAirport?.code ??
      leg.arrivalAirport?.code ??
      leg.destination ??
      leg.to ??
      leg.arrCity ??
      leg.arrival?.airportCode ??
      leg.arrAirportCode ??
      leg.arrivalAirportCode
  );
}

function normalizeLegArray(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input.filter(Boolean);
  return [input].filter(Boolean);
}

function extractFlightSegmentsForDisplay(order) {
  const or = safeOrderResponseObjectForNormalize(order);
  const roots = [
    or?.linkedFlights?.flight,
    or?.linkedflights?.flight,
    or?.linkedFlights,
    or?.linkedflights,
    order?.linkedFlights?.flight,
    order?.linkedflights?.flight,
    order?.linkedFlights,
    order?.linkedflights,
    or?.flight?.segments,
    or?.flight?.segment,
    or?.flight,
    or?.reservation?.flight?.segments,
    or?.reservation?.flight?.segment,
    or?.reservation?.flight,
  ];

  const out = [];
  const pushSegment = (seg) => {
    if (!seg) return;
    const legs = normalizeLegArray(seg?.legs ?? seg?.leg);
    if (legs.length > 0) {
      out.push({ legs });
      return;
    }
    if (depCodeFromLeg(seg) || arrCodeFromLeg(seg)) {
      out.push({ legs: [seg] });
    }
  };

  roots.forEach((root) => {
    if (!root) return;
    const list = Array.isArray(root) ? root : [root];
    list.forEach((item) => {
      if (!item) return;
      if (Array.isArray(item)) {
        if (item.length > 0) out.push({ legs: item.filter(Boolean) });
        return;
      }
      pushSegment(item);
    });
  });

  return out.filter((s) => Array.isArray(s.legs) && s.legs.length > 0);
}

function extractAirlineFromFlightOrder(order) {
  const segs = extractFlightSegmentsForDisplay(order);
  for (const seg of segs) {
    for (const leg of seg.legs) {
      const candidate =
        leg?.airline?.name ??
        leg?.airline?.code ??
        leg?.carrier ??
        leg?.carrierCode ??
        leg?.marketingCarrier ??
        leg?.operatingCarrier ??
        leg?.airlineId ??
        leg?.airlineCode ??
        seg?.airline?.name ??
        seg?.airline?.code;
      if (candidate != null && String(candidate).trim()) {
        return String(candidate).trim();
      }
    }
  }
  return "";
}

function shortClockFromDateTime(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  return `${h}:${m[2]}`;
}

function normalizeAirlineCode(value) {
  const code = String(value ?? "")
    .trim()
    .toUpperCase();
  if (!code) return "";
  if (!/^[A-Z0-9]{2,4}$/.test(code)) return "";
  return code;
}

/**
 * מסלול תצוגה להזמנת טיסה בלבד (LDS dealtype Flights / Flight).
 */
export function flightRouteLabelFromLdsOrder(order) {
  const segs = extractFlightSegmentsForDisplay(order);
  if (segs.length > 0) {
    const firstLeg = segs[0]?.legs?.[0];
    const origin = depCodeFromLeg(firstLeg);
    const chain = origin ? [origin] : [];
    segs.forEach((seg) => {
      const lastLeg = seg?.legs?.[seg.legs.length - 1];
      const destination = arrCodeFromLeg(lastLeg);
      if (!destination) return;
      if (chain[chain.length - 1] !== destination) chain.push(destination);
    });
    if (chain.length >= 2) return chain.join(" → ");
  }

  const pkg = getPackageOrderCardSummary(order);
  if (pkg?.displayTitle && String(pkg.displayTitle).trim() && pkg.displayTitle !== "חבילת נופש") {
    return String(pkg.displayTitle).trim();
  }

  const dep = order?.departure_city ?? order?.departureCity ?? order?.origin;
  const arr = order?.arrival_city ?? order?.arrivalCity ?? order?.destination;
  if (dep && arr) return `${dep} → ${arr}`;

  return order?.dep_date && order?.ret_date
    ? `טיסה: ${order.dep_date} – ${order.ret_date}`
    : "טיסה";
}

export function flightAirlineLabelFromLdsOrder(order) {
  const airline = extractAirlineFromFlightOrder(order);
  if (airline) return airline;
  return "חברת תעופה לא זמינה";
}

export function flightPrimaryAirlineCodeFromLdsOrder(order) {
  const segs = extractFlightSegmentsForDisplay(order);
  for (const seg of segs) {
    for (const leg of seg.legs) {
      const rawCode =
        leg?.airline?.code ??
        leg?.airlineId ??
        leg?.airlineCode ??
        leg?.carrierCode ??
        leg?.marketingCarrier ??
        leg?.operatingCarrier ??
        seg?.airline?.code;
      const normalized = normalizeAirlineCode(rawCode);
      if (normalized) return normalized;
    }
  }
  const fallback = normalizeAirlineCode(extractAirlineFromFlightOrder(order));
  return fallback || "";
}

export function flightTimesSummaryFromLdsOrder(order) {
  const segs = extractFlightSegmentsForDisplay(order);
  if (!segs.length) return "";
  const parts = [];
  segs.forEach((seg, idx) => {
    const first = seg?.legs?.[0];
    const last = seg?.legs?.[seg.legs.length - 1];
    const dep = shortClockFromDateTime(first?.depDate ?? first?.departureDateTime);
    const arr = shortClockFromDateTime(last?.arrDate ?? last?.arrivalDateTime);
    if (!dep && !arr) return;
    const range = dep && arr ? `${dep}–${arr}` : dep || arr;
    if (segs.length === 1) {
      parts.push(range);
    } else {
      const label = idx === 0 ? "הלוך" : idx === 1 ? "חזור" : `קטע ${idx + 1}`;
      parts.push(`${label}: ${range}`);
    }
  });
  return parts.join(" | ");
}

export function normalizePackageOrder(order, index = 0) {
  const mainContact = order?.passengersinfo?.[0]?.adults?.[0];
  const mainContactName = mainContact
    ? `${mainContact?.title || ""} ${mainContact?.firstname || ""} ${mainContact?.lastname || ""}`.trim()
    : "-";
  let hrCommission = 0;
  if (order.ourprice?.hrCommission) {
    hrCommission = parseFloat(order.ourprice.hrCommission) || 0;
  } else if (ldsCommisions(order)?.hr) {
    hrCommission = parseFloat(ldsCommisions(order).hr) || 0;
  }
  let handlingFee = 0;
  if (order.handlingfee && order.handle_fee_option) {
    if (order.handle_fee_option === "%") {
      const basePrice = order?.ourprice?.priceWithCreditCardFee || 0;
      handlingFee = (basePrice * parseFloat(order.handlingfee)) / 100;
    } else if (order.handle_fee_option === "+") {
      handlingFee = parseFloat(order.handlingfee) || 0;
    }
  } else if (order.handlingfee) {
    handlingFee = parseFloat(order.handlingfee) || 0;
  }
  const totalAmount = order?.ourprice?.priceWithCreditCardFee || 0;
  const ldsFinancial = resolveLdsFinancialState(order);
  const paidAmount = ldsFinancial.paidAmount ?? 0;
  const remainingAmount =
    ldsFinancial.remainingBalance ?? Math.max(totalAmount - paidAmount, 0);
  const pkgCard = getPackageOrderCardSummary(order);
  const hotelNameDisplay =
    typeof pkgCard.displayTitle === "string" && pkgCard.displayTitle.trim() !== ""
      ? pkgCard.displayTitle
      : "-";
  return {
    id: order.id ?? order.dealid,
    category: "חבילות",
    orderId: order.id || order.dealid || "-",
    bookingRef: order.booking_ref ?? order.bookingRef ?? null,
    hotelName: hotelNameDisplay,
    checkIn: order.dep_date || "-",
    checkOut: order.ret_date || "-",
    mainContact: mainContactName,
    status: getLdsOrderPrimaryBadgeText(order),
    totalAmount,
    paidAmount,
    remainingAmount,
    hrCommission,
    handlingFee,
    createdAt: order.created_at || "-",
    createdAtDate: order.created_at ? parseDate(order.created_at) : null,
    checkOutDate: order.ret_date ? parseDate(order.ret_date) : null,
    cityOrDestination: "",
    image: null,
    currency: order.currency || "ILS",
    agentName:
      order.user_first_name && order.user_last_name
        ? `${order.user_first_name} ${order.user_last_name}`
        : "-",
    paid: ldsFinancial.isFullyPaid,
    raw: order,
    key: `package-${order.id || order.dealid || index}`,
    totalPrice: totalAmount,
    sumPaid: paidAmount,
  };
}

export function normalizeFlightLdsOrder(order, index = 0) {
  const mainContact = order?.passengersinfo?.[0]?.adults?.[0];
  const mainContactName = mainContact
    ? `${mainContact?.title || ""} ${mainContact?.firstname || ""} ${mainContact?.lastname || ""}`.trim()
    : "-";
  let hrCommission = 0;
  if (order.ourprice?.hrCommission) {
    hrCommission = parseFloat(order.ourprice.hrCommission) || 0;
  } else if (ldsCommisions(order)?.hr) {
    hrCommission = parseFloat(ldsCommisions(order).hr) || 0;
  }
  let handlingFee = 0;
  if (order.handlingfee && order.handle_fee_option) {
    if (order.handle_fee_option === "%") {
      const basePrice = order?.ourprice?.priceWithCreditCardFee || 0;
      handlingFee = (basePrice * parseFloat(order.handlingfee)) / 100;
    } else if (order.handle_fee_option === "+") {
      handlingFee = parseFloat(order.handlingfee) || 0;
    }
  } else if (order.handlingfee) {
    handlingFee = parseFloat(order.handlingfee) || 0;
  }
  const totalAmount = order?.ourprice?.priceWithCreditCardFee || 0;
  const paidAmount = order.sumpaid || 0;
  const or = safeOrderResponseObjectForNormalize(order);
  const supplierId =
    or?.supplierDetails?.supplierId ??
    or?.supplier_details?.supplier_id ??
    order?.supplier_id ??
    order?.supplierId ??
    "";
  const routeTitle = flightRouteLabelFromLdsOrder(order);
  return {
    id: order.id ?? order.dealid,
    category: "טיסות",
    orderId: order.id || order.dealid || "-",
    bookingRef: order.booking_ref ?? order.bookingRef ?? null,
    hotelName: routeTitle,
    checkIn: order.dep_date || "-",
    checkOut: order.ret_date || "-",
    mainContact: mainContactName,
    status: getLdsOrderPrimaryBadgeText(order),
    totalAmount,
    paidAmount,
    remainingAmount: totalAmount - paidAmount,
    hrCommission,
    handlingFee,
    createdAt: order.created_at || "-",
    createdAtDate: order.created_at ? parseDate(order.created_at) : null,
    checkOutDate: order.ret_date ? parseDate(order.ret_date) : null,
    cityOrDestination: supplierId ? String(supplierId) : "",
    image: null,
    currency: order.currency || "ILS",
    agentName:
      order.user_first_name && order.user_last_name
        ? `${order.user_first_name} ${order.user_last_name}`
        : "-",
    paid: !!(order.paid || false),
    raw: order,
    key: `flight-${order.id || order.dealid || index}`,
    totalPrice: totalAmount,
    sumPaid: paidAmount,
  };
}

export function normalizeOrganizedTourOrder(order, index = 0) {
  const mainContact = order?.passengersinfo?.[0]?.adults?.[0];
  const mainContactName = mainContact
    ? `${mainContact?.title || ""} ${mainContact?.firstname || ""} ${mainContact?.lastname || ""}`.trim()
    : "-";
  let hrCommission = 0;
  if (order.ourprice?.hrCommission) {
    hrCommission = parseFloat(order.ourprice.hrCommission) || 0;
  } else if (ldsCommisions(order)?.hr) {
    hrCommission = parseFloat(ldsCommisions(order).hr) || 0;
  }
  let handlingFee = 0;
  if (order.handlingfee) {
    const handlingFeeValue = parseFloat(order.handlingfee) || 0;
    if (
      order.handlingfeepercentage === true &&
      handlingFeeValue > 0 &&
      handlingFeeValue <= 100
    ) {
      const basePrice = order?.ourprice?.priceWithCreditCardFee || 0;
      handlingFee = (basePrice * handlingFeeValue) / 100;
    } else {
      handlingFee = handlingFeeValue;
    }
  }
  const totalAmount = order?.ourprice?.priceWithCreditCardFee || 0;
  const paidAmount = order.sumpaid || 0;
  return {
    id: order.id ?? order.dealid,
    category: "טיולים מאורגנים",
    orderId: order.id || order.dealid || "-",
    bookingRef: order.booking_ref ?? order.bookingRef ?? null,
    hotelName: order?.order_response?.reservation?.hotel?.hotelName || "-",
    checkIn: order.dep_date || "-",
    checkOut: order.ret_date || "-",
    mainContact: mainContactName,
    status: getLdsOrderPrimaryBadgeText(order),
    totalAmount,
    paidAmount,
    remainingAmount: totalAmount - paidAmount,
    hrCommission,
    handlingFee,
    createdAt: order.created_at || "-",
    createdAtDate: order.created_at ? parseDate(order.created_at) : null,
    checkOutDate: order.ret_date ? parseDate(order.ret_date) : null,
    cityOrDestination: "",
    image: null,
    currency: order.currency || "ILS",
    agentName:
      order.user_first_name && order.user_last_name
        ? `${order.user_first_name} ${order.user_last_name}`
        : "-",
    paid: !!(order.paid || false),
    raw: order,
    key: `organized-${order.id || order.dealid || index}`,
    totalPrice: totalAmount,
    sumPaid: paidAmount,
  };
}

export function normalizeDynamicOrder(order, index = 0) {
  const agent = order.agent || {};
  const agentName =
    agent.firstname && agent.lastname
      ? `${agent.firstname} ${agent.lastname}`
      : "-";
  let hrCommission = 0;
  if (order.ourprice?.hrCommission) {
    hrCommission = parseFloat(order.ourprice.hrCommission) || 0;
  } else if (ldsCommisions(order)?.hr) {
    hrCommission = parseFloat(ldsCommisions(order).hr) || 0;
  }
  let handlingFee = 0;
  if (order.handlingfee && order.handle_fee_option) {
    if (order.handle_fee_option === "%") {
      const basePrice =
        order.ourprice?.ourTotalFare?.priceWithCommissionAndCreditCardFee ||
        0;
      handlingFee = (basePrice * parseFloat(order.handlingfee)) / 100;
    } else if (order.handle_fee_option === "+") {
      handlingFee = parseFloat(order.handlingfee) || 0;
    }
  } else if (order.handlingfee) {
    handlingFee = parseFloat(order.handlingfee) || 0;
  }
  const totalAmount =
    order.ourprice?.ourTotalFare?.priceWithCommissionAndCreditCardFee || 0;
  const paidAmount = order.sumpaid || 0;
  return {
    id: order.id ?? order.dealid,
    category: "חבילות דינמיות",
    orderId: order.id || order.dealid || "-",
    bookingRef: order.booking_ref ?? order.bookingRef ?? null,
    hotelName: order.hotel?.hotelname || order.hotel?.hotelName || "-",
    checkIn: order.check_in || "-",
    checkOut: order.check_out || "-",
    mainContact: "-",
    status: getLdsOrderPrimaryBadgeText(order),
    totalAmount,
    paidAmount,
    remainingAmount: totalAmount - paidAmount,
    hrCommission,
    handlingFee,
    createdAt: order.created_at || "-",
    createdAtDate: order.created_at ? parseDate(order.created_at) : null,
    checkOutDate: order.check_out ? parseDate(order.check_out) : null,
    cityOrDestination: "",
    image: null,
    currency: order.currency || "ILS",
    agentName,
    paid: !!(order.paid || false),
    raw: order,
    key: `dynamic-${order.id || order.dealid || index}`,
    totalPrice: totalAmount,
    sumPaid: paidAmount,
  };
}

export function buildNormalizedOrdersFromCategories(allOrdersByCategory) {
  const orders = [];
  const h = allOrdersByCategory?.hotels;
  if (Array.isArray(h)) {
    h.forEach((o, i) => orders.push(normalizeHotelOrder(o, i)));
  }
  const p = allOrdersByCategory?.packages;
  if (Array.isArray(p)) {
    p.forEach((o, i) => orders.push(normalizePackageOrder(o, i)));
  }
  const fl = allOrdersByCategory?.flights;
  if (Array.isArray(fl)) {
    fl.forEach((o, i) => orders.push(normalizeFlightLdsOrder(o, i)));
  }
  const ot = allOrdersByCategory?.organizedTours;
  if (Array.isArray(ot)) {
    ot.forEach((o, i) => orders.push(normalizeOrganizedTourOrder(o, i)));
  }
  const d = allOrdersByCategory?.dynamicPackages;
  if (Array.isArray(d)) {
    d.forEach((o, i) => orders.push(normalizeDynamicOrder(o, i)));
  }
  return orders;
}
